import { useState } from "react";
import { twMerge } from "tailwind-merge";
import { Button } from "../Button";
import { Type } from "../Type";
import { FileUploader } from "../FileUploader";

// Architecture Note: This GenericUploadInput intentionally does NOT wrap GenericUploadInputBase.
// Reason: The styled component uses specific UI components (FileUploader, Button, Type) for
// preview and error display that are tightly coupled with the design. GenericUploadInputBase
// requires render props for all UI elements which would make the styled version verbose.
// The logic (file validation, base64 conversion) is duplicated but simple enough to maintain.

export type UploadedFile = {
  /** Base64 encoded file data */
  file: string;
  /** Original filename */
  name: string;
  /** File size in bytes */
  size: number;
  /** MIME type */
  type: string;
  /** Original File object */
  original: File;
  /** Alt text for accessibility */
  altText?: string;
};

export type Props = {
  /** Field name for identification */
  name: string;
  /** Unique hash for file uploader element ID */
  hash: string;
  /** Maximum file size in bytes */
  maxFileSize: number;
  /** Currently uploaded file value */
  value?: UploadedFile | null;
  /** Change handler - receives uploaded file data */
  onChange?: (value: UploadedFile | null) => void;
  /** Blur handler */
  onBlur?: () => void;
  /** Whether the field has an error */
  error?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Map of allowed MIME types */
  allowedFileTypes?: Record<string, boolean>;
  /** Human-readable accepted file types description */
  acceptedFileTypes?: string;
  /** Optional resize function for images */
  resize?: (file: File) => Promise<File>;
  /** Transform function to customize the stored value */
  transform?: (file: File, base64: string) => UploadedFile | Promise<UploadedFile>;
  /** Render function for file preview */
  preview?: (value: UploadedFile) => React.ReactNode;
  /** Render function for file details */
  renderDetails?: (value: UploadedFile) => React.ReactNode;
  /** Label for the change button */
  changeButtonLabel?: string;
  /** Additional CSS classes */
  className?: string;
};

/**
 * Converts a File to base64 string
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
  });
}

/**
 * Generic file upload component.
 * Pure UI component - works with or without react-hook-form.
 *
 * @example
 * // Basic usage
 * <GenericUploadInput
 *   name="document"
 *   hash="doc-upload"
 *   maxFileSize={10 * 1024 * 1024}
 *   value={uploadedFile}
 *   onChange={setUploadedFile}
 *   allowedFileTypes={{ "application/pdf": true }}
 * />
 */
export function GenericUploadInput({
  name,
  hash,
  maxFileSize,
  value = null,
  onChange,
  onBlur: _onBlur,
  error,
  disabled,
  allowedFileTypes,
  acceptedFileTypes,
  resize,
  transform,
  preview,
  renderDetails,
  changeButtonLabel = "Change file",
  className,
}: Props) {
  const [errorText, setErrorText] = useState<string | null>(null);
  const [isChanged, setIsChanged] = useState<boolean>(false);

  const handleFileUpload = async (
    file: File,
    _onProgress: (progress: number) => void
  ): Promise<{ name: string; size: string; progress: number; error: boolean }> => {
    setErrorText(null);

    try {
      if (allowedFileTypes && !allowedFileTypes[file.type]) {
        setErrorText(`File type ${file.type} is not supported.`);
        return { name: file.name, size: String(file.size), progress: 0, error: true };
      }

      const resized = resize ? await resize(file) : file;
      if (resized.size > maxFileSize) {
        setErrorText("File too large.");
        return { name: file.name, size: String(file.size), progress: 0, error: true };
      }

      const base64 = await fileToBase64(resized);

      const finalValue: UploadedFile = transform
        ? await transform(resized, base64)
        : {
            file: base64,
            name: resized.name,
            size: resized.size,
            type: resized.type,
            original: resized,
          };

      onChange?.(finalValue);
      setIsChanged(true);
      return { name: finalValue.name, size: String(finalValue.size), progress: 100, error: false };
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : "Upload failed";
      setErrorText(errorMessage);
      return { name: file.name, size: String(file.size), progress: 0, error: true };
    }
  };

  return (
    <div
      className={twMerge(
        "border-none p-0 flex flex-col gap-2 shadow-none",
        error && "border-error-600",
        disabled && "opacity-50 pointer-events-none",
        className
      )}
      data-testid={`upload-input-${name}`}
    >
      {value?.file && (
        <div className="mt-2 flex flex-col items-start gap-2">
          {preview?.(value)}
          {renderDetails?.(value)}

          <Button
            variant="secondary"
            data-testid={`${name}-change-button`}
            onClick={() => document.getElementById(`file-upload${hash}`)?.click()}
            disabled={disabled}
          >
            {changeButtonLabel}
          </Button>

          {isChanged && (
            <Type variant="h6" className="text-success">
              Click "Save" to apply changes
            </Type>
          )}
        </div>
      )}

      {errorText && (
        <Type variant="caption" className="text-error mt-2" role="alert">
          {errorText}
        </Type>
      )}

      <FileUploader
        hash={hash}
        uploadFileHandler={handleFileUpload}
        className={value?.file ? "hidden" : ""}
        allowedFileTypes={allowedFileTypes}
        acceptedFileTypes={acceptedFileTypes}
      />
    </div>
  );
}

export default GenericUploadInput;

